/* Subscribe flow (prototype subscribeModal): plan → bank transfer details → upload receipt (AI read) → submit. */
import { useEffect, useId, useState, type ChangeEvent } from "react";
import { ApiError } from "../../data/provider";
import { useAiExtract, useSubmitPayment, useToday, useUploadFile } from "../../data/hooks";
import type { BillingView, Cycle } from "../../data/types";
import { useI18n } from "../../i18n";
import { moneyText } from "../../lib/format";
import { Affix, Alert, Button, CopyValue, Field, Icon, Input, Kv, Modal, Money, Seg, Select, cx, errMsg, useToast } from "../../ui";
import { seatsError } from "../public/components/PriceCalculator";
import { PLANS, quote, type PlanName } from "../public/components/pricingData";
import { SENDER_BANKS, refPrefix, samplePngBlob, sampleReceiptSvg } from "./receipt";

interface Form {
  plan: PlanName; cycle: Cycle; seats: number | ""; ai: boolean;
  file: string | null; preview: string | null; receiptKey: string | null; conf: number | null;
  paid: string; date: string; bank: string; sender: string; ref: string;
}

export function SubscribeModal({ view, initialPlan, onClose }: { view: BillingView; initialPlan?: string; onClose: () => void }) {
  const { t, L, lang } = useI18n();
  const toast = useToast();
  const upload = useUploadFile();
  const extract = useAiExtract();
  const submit = useSubmitPayment();
  const sub = view.subscription;
  const today = useToday();
  const plan0 = (PLANS as readonly string[]).includes(initialPlan ?? "") ? (initialPlan as PlanName) : (PLANS as readonly string[]).includes(sub.plan) ? (sub.plan as PlanName) : "Business";
  const [step, setStep] = useState(0);
  const [f, setF] = useState<Form>({ plan: plan0, cycle: sub.cycle, seats: sub.seats, ai: sub.aiPack > 0, file: null, preview: null, receiptKey: null, conf: null, paid: "", date: today, bank: SENDER_BANKS[0], sender: view.tenant.name, ref: "" });
  const [fe, setFe] = useState<Record<string, string>>({});
  const [reading, setReading] = useState(false);
  const seatsId = useId(), fileId = useId(), aiId = useId(), paidId = useId(), dateId = useId(), refId = useId(), senderId = useId(), bankId = useId();

  const seatsErr = seatsError(f.seats, L);
  const a = quote(view.planPrices, f.plan, f.cycle, typeof f.seats === "number" ? f.seats : 1, f.ai ? 1 : 0);
  const prefix = refPrefix(view.tenant.cr);
  const banks = view.bankAccounts.filter((b) => b.active);
  const set = (p: Partial<Form>) => setF((x) => ({ ...x, ...p }));

  useEffect(() => () => { if (f.preview?.startsWith("blob:")) URL.revokeObjectURL(f.preview); }, [f.preview]);

  const paidNum = Number(f.paid);
  const paidErr = fe.paid || (f.paid && Number.isFinite(paidNum) && Math.abs(paidNum - a.total) > 0.5 ? L("Doesn't match the total of ", "لا يطابق الإجمالي ") + moneyText(a.total, lang) : undefined);
  const refErr = f.ref && !f.ref.trim().toUpperCase().startsWith(prefix) ? L("Expected a code starting with " + prefix, "المتوقع رمز يبدأ بـ " + prefix) : undefined;

  /** Upload to S3 (folder "receipts"), then let AI read amount/date/reference. */
  const readReceipt = async (file: Blob, name: string, preview: string | null, sample?: string) => {
    set({ file: name, preview, receiptKey: null, conf: null });
    setReading(true);
    try {
      const ref = await upload.mutateAsync({ file, name, folder: "receipts" });
      set({ receiptKey: ref.key });
      try {
        const r = await extract.mutateAsync({ feature: "receipt", fileKey: ref.key, sample });
        const x = r.fields;
        const bank = typeof x.bank === "string" && SENDER_BANKS.includes(x.bank) ? x.bank : undefined;
        const date = typeof x.date === "string" && x.date <= today ? x.date : undefined;
        setF((cur) => ({
          ...cur, conf: Math.round(r.confidence * 100),
          paid: x.amount != null ? String(x.amount) : cur.paid,
          date: date ?? cur.date, bank: bank ?? cur.bank,
          sender: typeof x.sender === "string" && x.sender ? x.sender : cur.sender,
          ref: typeof x.ref === "string" && x.ref ? x.ref : cur.ref,
        }));
      } catch (e) {
        toast(L("AI couldn't read the receipt — please enter the details. ", "تعذرت قراءة الإيصال تلقائيًا — يرجى إدخال التفاصيل. ") + errMsg(e), "info");
      }
    } catch (e) {
      set({ file: null, preview: null });
      toast(errMsg(e), "bad");
    } finally { setReading(false); }
  };

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const preview = /^image\//.test(file.type) ? URL.createObjectURL(file) : null;
    void readReceipt(file, file.name, preview);
  };
  const useSample = () => {
    const prev = sampleReceiptSvg({ paid: a.total, ref: prefix + "-…", date: today, bank: f.bank });
    void readReceipt(samplePngBlob(), "IMG_transfer_receipt.png", prev, "default");
  };

  const doSubmit = async () => {
    const e: Record<string, string> = {};
    if (!(paidNum > 0)) e.paid = L("Enter the amount you transferred", "أدخل المبلغ المحوّل");
    if (!f.date) e.date = L("Enter the transfer date", "أدخل تاريخ التحويل");
    else if (f.date > today) e.date = L("The date can't be in the future", "لا يمكن أن يكون التاريخ في المستقبل");
    if (!f.sender.trim()) e.sender = L("Enter the sender name", "أدخل اسم المحوّل");
    setFe(e);
    if (Object.keys(e).length) return;
    try {
      const p = await submit.mutateAsync({ plan: f.plan, cycle: f.cycle, seats: Number(f.seats), aiPack: f.ai ? 1 : 0, bank: f.bank, sender: f.sender.trim(), paid: paidNum, date: f.date, receiptKey: f.receiptKey ?? undefined });
      toast(L("Receipt submitted · " + p.id + " — we'll verify it within one business day", "تم إرسال الإيصال · " + p.id + " — سنتحقق منه خلال يوم عمل"));
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) { toast(L("You already have a payment waiting for verification.", "لديك دفعة بانتظار التحقق بالفعل."), "bad"); onClose(); return; }
      if (err instanceof ApiError && err.fields) setFe(err.fields);
      toast(errMsg(err), "bad");
    }
  };

  const steps = [L("Plan", "الباقة"), L("Bank transfer", "التحويل"), L("Upload receipt", "رفع الإيصال")];
  const head = (
    <ol className="wz" style={{ marginBottom: 16 }}>
      {steps.map((s, i) => <li key={i} className={i < step ? "done" : i === step ? "cur" : ""}><span className="wb" /><span className="wl">{i < step && <Icon name="check" className="s" />}{s}</span></li>)}
    </ol>
  );

  let body = null, foot = null;
  if (step === 0) {
    body = (
      <div className="stack">
        <div className="g g-3" style={{ gap: 8 }} role="radiogroup" aria-label={L("Plan", "الباقة")}>
          {PLANS.map((pl) => (
            <button key={pl} type="button" role="radio" aria-checked={f.plan === pl} className={cx("tpl", f.plan === pl && "on")} style={{ padding: 14 }} onClick={() => set({ plan: pl })} data-testid={"pay-plan-" + pl}>
              <b>{pl}</b><span className="num">{view.planPrices[pl]} <span className="xs muted">{L("SAR / employee / mo", "ر.س / موظف / شهر")}</span></span>
            </button>
          ))}
        </div>
        <div className="form2">
          <Field label={L("Billing cycle", "دورة الفوترة")}><Seg label={L("Billing cycle", "دورة الفوترة")} items={[["m", t("Monthly")], ["y", t("Annually") + " −20%"]]} value={f.cycle} onChange={(cycle) => set({ cycle })} /></Field>
          <Field label={L("Employees", "عدد الموظفين")} htmlFor={seatsId} err={seatsErr} hint={L("Your trial covers " + sub.seats + " employees", "تجربتك تغطي " + sub.seats + " موظفًا")}>
            <Input id={seatsId} className="num" type="number" min={1} value={f.seats} bad={!!seatsErr} onChange={(e) => set({ seats: e.target.value === "" ? "" : Number(e.target.value) })} data-testid="pay-seats" />
          </Field>
        </div>
        <label className="check" htmlFor={aiId}><input id={aiId} type="checkbox" checked={f.ai} onChange={(e) => set({ ai: e.target.checked })} data-testid="pay-ai" /> {L("Add AI credits pack (+1,000 credits/month · SAR 99)", "إضافة باقة رصيد الذكاء الاصطناعي (+1,000 رصيد شهريًا · 99 ر.س)")}</label>
        <section className="card pad" data-testid="pay-summary">
          <div className="card-b"><Kv items={[[L("Subtotal", "المجموع"), <Money key="a" value={a.amount} />], [L("VAT 15%", "ضريبة القيمة المضافة 15%"), <Money key="v" value={a.vat} />], [<b key="t">{t("Total")}</b>, <b key="tv" data-testid="pay-total"><Money value={a.total} /></b>]]} /></div>
        </section>
      </div>
    );
    foot = <><Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button><Button variant="pri" end="chevron-right" disabled={!!seatsErr} onClick={() => setStep(1)} data-testid="pay-next-1">{t("Continue")}</Button></>;
  } else if (step === 1) {
    body = (
      <div className="stack">
        <Alert tone="info" tint title={L("Transfer exactly ", "حوّل المبلغ بالضبط ") + moneyText(a.total, lang)} desc={L("Write the reference code in the transfer description so we can match it quickly.", "اكتب رمز المرجع في وصف التحويل لنتمكن من المطابقة بسرعة.")} />
        <div className="refbox"><span className="xs muted">{L("Your reference code", "رمز المرجع الخاص بك")}</span><CopyValue label="" value={prefix} testId="pay-ref-code" /></div>
        {banks.length === 0 && <Alert tone="warn" title={L("No receiving account is available right now", "لا يوجد حساب استلام متاح حاليًا")} desc={L("Please contact StartHR support.", "يرجى التواصل مع دعم ستارت HR.")} />}
        {banks.map((b) => (
          <div key={b.iban} className="bankcard" data-testid="bankcard">
            <div className="row between"><b>{L(b.bank, b.bankAr)}</b><span className="chip ok">{L("Saudi riyal account", "حساب بالريال")}</span></div>
            <Kv items={[[L("Beneficiary", "المستفيد"), L(b.name, b.nameAr)], [L("Account no.", "رقم الحساب"), <span key="a" className="num">{b.acct}</span>]]} />
            <CopyValue label="IBAN" value={b.iban} testId={"iban-" + b.iban.replace(/\s/g, "")} />
          </div>
        ))}
        <p className="xs muted" style={{ margin: 0 }}>{L("Instant transfers (SARIE) usually arrive in minutes. Standard transfers can take up to one business day.", "التحويلات الفورية تصل عادة خلال دقائق، والعادية قد تستغرق يوم عمل.")}</p>
      </div>
    );
    foot = <><Button icon="chevron-left" onClick={() => setStep(0)}>{t("Back")}</Button><Button variant="pri" end="chevron-right" onClick={() => setStep(2)} data-testid="pay-next-2">{L("I've made the transfer", "قمت بالتحويل")}</Button></>;
  } else {
    body = (
      <div className="stack">
        <label className="drop" htmlFor={fileId} style={{ cursor: "pointer" }} data-testid="pay-drop">
          {f.preview ? <img src={f.preview} alt={L("Receipt preview", "معاينة الإيصال")} style={{ maxHeight: 180, borderRadius: 8 }} /> : <Icon name="upload" className="l" />}
          <b>{f.file ?? L("Upload the transfer receipt", "ارفع إيصال التحويل")}</b>
          <span className="sm">{L("Screenshot or PDF from your bank app · up to 10 MB", "لقطة شاشة أو PDF من تطبيق البنك · حتى 10 ميغابايت")}</span>
          <input type="file" id={fileId} accept="image/*,application/pdf" onChange={onFile} hidden data-testid="pay-file" />
        </label>
        {!f.file && <Button variant="ghost sm" icon="eye" onClick={useSample} data-testid="pay-sample">{L("Use a sample receipt", "استخدم إيصالًا تجريبيًا")}</Button>}
        {reading && <div className="aithink" data-testid="pay-reading"><span className="aidots"><i /><i /><i /></span>{L("Reading your receipt…", "جارٍ قراءة الإيصال…")}</div>}
        {f.conf != null && <Alert tone="ok" tint testId="pay-ai-read" title={<><span className="aitag"><Icon name="sparkle" className="s" />AI</span> {L("Details read from your receipt", "تمت قراءة التفاصيل من الإيصال")}</>} desc={L("Check them before submitting. Confidence ", "راجعها قبل الإرسال. الدقة ") + f.conf + "%"} />}
        <div className="form2">
          <Field label={L("Amount transferred", "المبلغ المحوّل")} htmlFor={paidId} err={paidErr}>
            <Affix pre={L("SAR", "ر.س")}><Input id={paidId} className="num" inputMode="decimal" value={f.paid} bad={!!paidErr} onChange={(e) => { set({ paid: e.target.value }); setFe({ ...fe, paid: "" }); }} data-testid="pay-paid" /></Affix>
          </Field>
          <Field label={L("Transfer date", "تاريخ التحويل")} htmlFor={dateId} err={fe.date}>
            <Input id={dateId} type="date" max={today} value={f.date} bad={!!fe.date} onChange={(e) => { set({ date: e.target.value }); setFe({ ...fe, date: "" }); }} data-testid="pay-date" />
          </Field>
          <Field label={L("From bank", "من بنك")} htmlFor={bankId} err={fe.bank}>
            <Select id={bankId} value={f.bank} onChange={(e) => set({ bank: e.target.value })} options={SENDER_BANKS} data-testid="pay-bank" />
          </Field>
          <Field label={L("Sender name", "اسم المحوّل")} htmlFor={senderId} err={fe.sender}>
            <Input id={senderId} value={f.sender} bad={!!fe.sender} onChange={(e) => { set({ sender: e.target.value }); setFe({ ...fe, sender: "" }); }} data-testid="pay-sender" />
          </Field>
          <Field label={L("Reference in transfer note", "المرجع في وصف التحويل")} htmlFor={refId} err={refErr} className="span-all">
            <Input id={refId} dir="ltr" value={f.ref} placeholder={prefix + "-…"} bad={!!refErr} onChange={(e) => set({ ref: e.target.value })} data-testid="pay-ref" />
          </Field>
        </div>
      </div>
    );
    foot = <><Button icon="chevron-left" onClick={() => setStep(1)}>{t("Back")}</Button><Button variant="pri" icon="check" loading={submit.isPending} disabled={!f.file || !f.paid || reading} onClick={doSubmit} data-testid="pay-submit">{L("Submit for verification", "إرسال للتحقق")}</Button></>;
  }

  return (
    <Modal title={L("Subscribe · ", "الاشتراك · ") + f.plan} className="wide" onClose={onClose} foot={foot} testId="subscribe-modal">
      {head}{body}
    </Modal>
  );
}
